import { Link } from "react-router-dom";
import { Clock, ArrowRight, Newspaper } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";
import useSEO from "@/hooks/useSEO";

export default function Blog() {
    useSEO({
        title: "Blog · Massothérapie & bien-être — Jérémy Tessier",
        description: "Articles pratiques sur le massage, la sophrologie, la gestion du stress et le bien-être — par Jérémy Tessier, massothérapeute à Blainville.",
        canonical: "https://massoclic.ca/blog",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Blog Jérémy Tessier — Massothérapie",
            url: "https://massoclic.ca/blog",
            blogPost: BLOG_POSTS.map((p) => ({
                "@type": "BlogPosting",
                headline: p.title,
                url: `https://massoclic.ca/blog/${p.slug}`,
                datePublished: p.date,
                image: p.image,
                author: { "@type": "Person", name: "Jérémy Tessier" },
                description: p.excerpt,
            })),
        },
    });

    return (
        <main data-testid="blog-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Newspaper size={14} className="text-[#3D8C95]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Blog & articles
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Comprendre son corps,{" "}
                        <span className="italic text-[#E07A5F]">apaiser son esprit</span>.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed">
                        Articles pratiques sur le massage, la sophrologie, la respiration et le bien-être — pour prolonger les bienfaits de vos séances entre deux rendez-vous.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {BLOG_POSTS.map((p) => (
                        <Link
                            to={`/blog/${p.slug}`}
                            key={p.slug}
                            data-testid={`blog-card-${p.slug}`}
                            className="group rounded-3xl overflow-hidden bg-white border border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)] hover:shadow-[0_20px_60px_rgba(44,42,41,0.1)] transition-all duration-500 hover:-translate-y-1"
                        >
                            <div className="aspect-[16/10] overflow-hidden">
                                <img
                                    src={p.image}
                                    alt={p.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-2 text-xs text-[#6E6862] mb-3">
                                    <Clock size={12} />
                                    <span>{p.readTime} min de lecture</span>
                                    <span>·</span>
                                    <span>
                                        {new Date(p.date).toLocaleDateString("fr-CA", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </span>
                                </div>
                                <h2 className="font-display text-2xl text-[#2C2A29] leading-tight group-hover:text-[#E07A5F] transition-colors">
                                    {p.title}
                                </h2>
                                <p className="mt-3 text-sm text-[#6E6862] leading-relaxed line-clamp-3">
                                    {p.excerpt}
                                </p>
                                <div className="mt-5 flex items-center gap-2 text-sm font-medium text-[#3D8C95] group-hover:gap-3 transition-all">
                                    Lire l&apos;article <ArrowRight size={14} />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </main>
    );
}
